import { SubscriptionPlanDto, SubscriptionType } from '@editar-pdf/api';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const CURRENCY_LABEL = '$';

export const BASE_TARIFF = SubscriptionType.TRIAL;

export function useBillingInfoGetter() {
  const { t } = useTranslation('web', { keyPrefix: 'tariffs.billingInfo' });

  return useCallback(
    (tariff: SubscriptionType, plans?: SubscriptionPlanDto[]) => {
      const trialPlan = plans?.find((v) => v.subscriptionType === SubscriptionType.TRIAL);
      const monthPlan = plans?.find((v) => v.subscriptionType === SubscriptionType.MONTHLY);
      const yearPlan = plans?.find((v) => v.subscriptionType === SubscriptionType.YEARLY);

      switch (tariff) {
        case SubscriptionType.FREE:
          return t('FREE');
        case SubscriptionType.TRIAL:
          return trialPlan && monthPlan
            ? t('TRIAL', { currency: CURRENCY_LABEL, initialCharge: trialPlan.price, charge: monthPlan.price })
            : null;
        case SubscriptionType.MONTHLY:
          return monthPlan ? t('MONTHLY', { currency: CURRENCY_LABEL, initialCharge: monthPlan.price }) : null;
        case SubscriptionType.YEARLY:
          return yearPlan ? t('YEARLY', { currency: CURRENCY_LABEL, initialCharge: yearPlan.price }) : null;
        default:
          return null;
      }
    },
    [t],
  );
}

/**
 * What the card is actually charged for this plan.
 */
export function getBaseCost(plan: SubscriptionPlanDto) {
  return plan.price;
}

/**
 * Weeks in each recurring billing period, using the 4-weeks-a-month convention (48 a year) the
 * sister sites quote, so a monthly $29.95 headlines as $7.49 a week and a yearly $59.95 as $1.25.
 */
const WEEKS_PER_PERIOD: Partial<Record<SubscriptionType, number>> = {
  [SubscriptionType.MONTHLY]: 4,
  [SubscriptionType.YEARLY]: 48,
};

/**
 * The plan's price spread over a week, which is what the cards headline: it is the only figure
 * that compares monthly against yearly directly. Never billed, so every surface showing it also
 * shows the real charge. Undefined for the plans this cannot describe: the trial is a fixed
 * three days, not a recurring period, and FREE is not charged at all.
 */
export function getWeeklyPrice(plan: SubscriptionPlanDto) {
  const weeks = WEEKS_PER_PERIOD[plan.subscriptionType];
  if (!weeks) {
    return undefined;
  }

  return Math.round((plan.price / weeks) * 100) / 100;
}

/**
 * The yearly saving against paying monthly for a year, as a whole percentage. Derived from the
 * live prices rather than hardcoded, so the badge cannot contradict the numbers beside it.
 * Returns 0 — no badge — when either price is missing.
 */
export function calcDiscount(tariff: SubscriptionType, plans?: SubscriptionPlanDto[]) {
  if (tariff !== SubscriptionType.YEARLY) {
    return 0;
  }

  const monthly = plans?.find((v) => v.subscriptionType === SubscriptionType.MONTHLY)?.price;
  const yearly = plans?.find((v) => v.subscriptionType === SubscriptionType.YEARLY)?.price;
  if (!monthly || !yearly) {
    return 0;
  }

  return Math.round((1 - yearly / (monthly * 12)) * 100);
}

export enum TARIFF_ACTIONS {
  UPGRADE = 'UPGRADE',
  DOWNGRADE = 'DOWNGRADE',
}

export function getTariffAction(tariff: SubscriptionType, plans: SubscriptionPlanDto[]) {
  const currentTariff = plans.find((v) => v.active);
  if (!currentTariff || currentTariff.subscriptionType === tariff) {
    return undefined;
  }

  if (currentTariff.subscriptionType === SubscriptionType.YEARLY) {
    return TARIFF_ACTIONS.DOWNGRADE;
  }

  if (currentTariff.subscriptionType === SubscriptionType.MONTHLY) {
    if (tariff === SubscriptionType.YEARLY) {
      return TARIFF_ACTIONS.UPGRADE;
    } else {
      return TARIFF_ACTIONS.DOWNGRADE;
    }
  }

  if (currentTariff.subscriptionType === SubscriptionType.TRIAL) {
    if (tariff === SubscriptionType.FREE) {
      return TARIFF_ACTIONS.DOWNGRADE;
    } else {
      return TARIFF_ACTIONS.UPGRADE;
    }
  }

  return TARIFF_ACTIONS.UPGRADE;
}

export function useButtonLabelGetter() {
  const { t } = useTranslation('web', { keyPrefix: 'tariffs.action' });

  return useCallback(
    (tariff?: SubscriptionType, plans?: SubscriptionPlanDto[]) => {
      if (!plans || !tariff) {
        return t('blank');
      }

      // Nobody is on a plan: every card is an offer, not a move between plans. Without this the
      // undefined action below falls through to "Current" on all three cards.
      if (!plans.some((v) => v.active)) {
        return t('subscribe');
      }

      const action = getTariffAction(tariff, plans);

      if (action === TARIFF_ACTIONS.DOWNGRADE) {
        return t('downgrade');
      } else if (action === TARIFF_ACTIONS.UPGRADE) {
        return t('upgrade');
      } else {
        return t('current');
      }
    },
    [t],
  );
}
